import { useCallback } from 'react'
import { useCookies } from 'react-cookie'
import { getSharedEnv } from '../../shared/lib/env'
import { CookieSetOptions } from 'universal-cookie'

export enum CookieName {
  TRACKED_TRANSACTION = 'cookieTrackedTransaction',
  DGC_CART = 'cookieDGCCartName',
  DGC_CART_PREVIOUS = 'cookieDGCCartPreviousName',
  DGC_CART_COUNT = 'cookieDGCCartCountName',
  CART = 'cookieCartName',
  CART_PREVIOUS = 'cookieCartPreviousName',
  CART_COUNT = 'cookieCartCountName',
  SUBURB = 'cookieSuburbName',
  CENTRE = 'cookieCentreName',
  ID_TOKEN = 'cookieIdTokenName',
  CREDIT_CARD_CHECKBOX = 'cookieCreditCardCheckbox',
  DELIVERY_ADDRESS_CHECKBOX = 'cookieDeliveryAddressCheckbox',
  PER_PAGE = 'cookieItemsPerPage',
  USER_FLOW = 'cookieUserFlow',
}

const COOKIE_OPTIONS: CookieSetOptions = {
  path: '/',
  domain: getSharedEnv('sharedCookiesDomain'),
  sameSite: 'strict',
  maxAge: getSharedEnv('cookieMaxAgeInSeconds'),
  secure: getSharedEnv('isDeployed'),
}

export function useCookie(
  cookieName: CookieName
): [string | undefined, (cookieValue: string) => void, () => void] {
  const cookieEnvValue = getSharedEnv(cookieName)
  const [cookies, setCookie, removeCookie] = useCookies([cookieEnvValue])
  // There are cases where useCookies auto-transforms strings it identifies
  // as a JSON representation. Forcing the cookie value as a string, enforces
  // that we return the correct type of string | undefined.
  const currentCookie = cookies[cookieEnvValue]
  const cookieValue =
    currentCookie && typeof currentCookie === 'string'
      ? currentCookie
      : JSON.stringify(currentCookie)

  const setCookieValue = useCallback(
    (cookieValue: string) => {
      setCookie(cookieEnvValue, cookieValue, COOKIE_OPTIONS)
    },
    [cookieEnvValue, setCookie]
  )

  const removeCookieValue = useCallback(() => {
    removeCookie(cookieEnvValue, COOKIE_OPTIONS)
  }, [cookieEnvValue, removeCookie])

  return [cookieValue, setCookieValue, removeCookieValue]
}
